import type { GroupChatTransport } from './group-chat';

interface Listener {
  change(): void;
  error(error: Error): void;
}
interface Subscription {
  listeners: Set<Listener>;
  generation: number;
  stop?: () => void;
  retry(): void;
}
const subscriptions = new WeakMap<GroupChatTransport, Map<string, Subscription>>();

/** Share one live connection between a group's profile summary and open chat. */
export function watchGroupChat(
  transport: GroupChatTransport,
  viewerId: string,
  groupId: string,
  listener: Listener,
) {
  let groups = subscriptions.get(transport);
  if (!groups) {
    groups = new Map();
    subscriptions.set(transport, groups);
  }
  const key = JSON.stringify([viewerId, groupId]);
  let entry = groups.get(key);
  if (!entry) {
    const created: Subscription = {
      listeners: new Set(),
      generation: 0,
      retry() {
        created.stop?.();
        created.stop = undefined;
        const generation = ++created.generation;
        void transport
          .subscribe(
            groupId,
            () => {
              if (generation === created.generation)
                for (const subscriber of created.listeners) subscriber.change();
            },
            (error) => {
              if (generation === created.generation)
                for (const subscriber of created.listeners) subscriber.error(error);
            },
          )
          .then((stop) => {
            if (generation !== created.generation || created.listeners.size === 0) stop();
            else created.stop = stop;
          })
          .catch((error: unknown) => {
            if (generation !== created.generation) return;
            const failure = error instanceof Error ? error : new Error(String(error));
            for (const subscriber of created.listeners) subscriber.error(failure);
          });
      },
    };
    entry = created;
    groups.set(key, entry);
    entry.listeners.add(listener);
    entry.retry();
  } else entry.listeners.add(listener);
  const current = entry;
  return {
    retry: () => current.retry(),
    dispose() {
      current.listeners.delete(listener);
      if (current.listeners.size === 0) {
        ++current.generation;
        current.stop?.();
        groups.delete(key);
      }
    },
  };
}
